<template>
  <div class="power-room-electric">
    <div class="electric-toolbar">
      <el-form :inline="true" size="small" :model="query">
        <el-form-item label="配电室">
          <el-input v-model="query.roomCode" class="w-170" placeholder="配电室编码" />
        </el-form-item>
        <el-form-item label="柜号">
          <el-input v-model="query.cabinetCode" class="w-120" placeholder="如 Z34" clearable />
        </el-form-item>
        <el-form-item label="用电类型">
          <el-select v-model="query.usageType" class="w-130" clearable placeholder="全部">
            <el-option label="生产用电" value="PRODUCTION" />
            <el-option label="生活用电" value="LIVING" />
          </el-select>
        </el-form-item>
        <el-form-item label="时间范围">
          <el-date-picker
            v-model="timeRange"
            class="w-360"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            :default-time="['00:00:00', '23:59:59']"
          />
        </el-form-item>
        <el-form-item label="间隔">
          <el-input-number v-model="query.intervalMinutes" class="w-130" :min="1" :max="1440" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="loadAll">查询</el-button>
          <el-button icon="el-icon-refresh" @click="refreshCurrent">当前值</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="electric-import">
      <el-form :inline="true" size="small">
        <el-form-item label="104通道">
          <el-input v-model="importForm.channelCode" class="w-170" placeholder="通道编码" />
        </el-form-item>
        <el-form-item label="默认系数">
          <el-input-number v-model="importForm.defaultScaleFactor" class="w-150" :min="0" :step="0.001" :precision="3" />
        </el-form-item>
        <el-form-item label="试导入">
          <el-switch v-model="importForm.dryRun" />
        </el-form-item>
        <el-form-item>
          <el-upload
            action=""
            accept=".xlsx,.xls"
            :show-file-list="false"
            :http-request="handleImport"
          >
            <el-button icon="el-icon-upload2">导入电度点表</el-button>
          </el-upload>
        </el-form-item>
        <el-form-item v-if="importResult">
          <span class="import-result">
            共 {{ importResult.totalRows || 0 }} 行，新增 {{ importResult.insertCount || 0 }}，更新 {{ importResult.updateCount || 0 }}，跳过 {{ importResult.skipCount || 0 }}
          </span>
        </el-form-item>
      </el-form>
    </div>

    <div class="electric-summary">
      <div class="summary-item">
        <span>总用电</span>
        <strong>{{ formatNumber(totalUsage) }}</strong>
        <em>kWh</em>
      </div>
      <div class="summary-item">
        <span>生产用电</span>
        <strong>{{ formatNumber(productionUsage) }}</strong>
        <em>kWh</em>
      </div>
      <div class="summary-item">
        <span>生活用电</span>
        <strong>{{ formatNumber(livingUsage) }}</strong>
        <em>kWh</em>
      </div>
      <div class="summary-item">
        <span>当前柜数</span>
        <strong>{{ currentRows.length }}</strong>
        <em>个</em>
      </div>
    </div>

    <el-row :gutter="12">
      <el-col :xs="24" :lg="13">
        <div class="electric-panel">
          <div class="panel-title">当前累计值</div>
          <el-table :data="currentRows" size="small" border height="360" v-loading="currentLoading">
            <el-table-column prop="cabinetCode" label="柜号" width="80" />
            <el-table-column prop="displayName" label="名称" min-width="180" show-overflow-tooltip />
            <el-table-column prop="usageTypeName" label="类型" width="90" />
            <el-table-column prop="epPlus" label="EP+" width="100" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.epPlus) }}</template>
            </el-table-column>
            <el-table-column prop="epMinus" label="EP-" width="100" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.epMinus) }}</template>
            </el-table-column>
            <el-table-column prop="eqPlus" label="EQ+" width="100" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.eqPlus) }}</template>
            </el-table-column>
            <el-table-column prop="lastCollectTime" label="采集时间" width="160" />
          </el-table>
        </div>
      </el-col>
      <el-col :xs="24" :lg="11">
        <div class="electric-panel">
          <div class="panel-title">区间汇总</div>
          <el-table :data="summaryRows" size="small" border height="360" v-loading="summaryLoading">
            <el-table-column prop="groupCode" label="分组" width="100" />
            <el-table-column prop="groupName" label="名称" min-width="160" show-overflow-tooltip />
            <el-table-column prop="usageTypeName" label="类型" width="90" />
            <el-table-column prop="usageKwh" label="用电量(kWh)" width="130" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.usageKwh) }}</template>
            </el-table-column>
            <el-table-column prop="quality" label="质量" width="90" />
          </el-table>
        </div>
      </el-col>
    </el-row>

    <div class="electric-panel usage-panel">
      <div class="panel-title">分柜用电明细</div>
      <el-table :data="usageRows" size="small" border height="420" v-loading="usageLoading">
        <el-table-column prop="cabinetCode" label="柜号" width="80" />
        <el-table-column prop="displayName" label="名称" min-width="200" show-overflow-tooltip />
        <el-table-column prop="usageTypeName" label="类型" width="90" />
        <el-table-column prop="startTime" label="开始时间" width="160" />
        <el-table-column prop="endTime" label="结束时间" width="160" />
        <el-table-column prop="startEpPlus" label="开始EP+" width="110" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.startEpPlus) }}</template>
        </el-table-column>
        <el-table-column prop="endEpPlus" label="结束EP+" width="110" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.endEpPlus) }}</template>
        </el-table-column>
        <el-table-column prop="usageKwh" label="用电量(kWh)" width="130" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.usageKwh) }}</template>
        </el-table-column>
        <el-table-column prop="calcMethod" label="算法" width="120" />
        <el-table-column prop="quality" label="质量" width="90" />
      </el-table>
    </div>

    <el-dialog title="导入错误明细" :visible.sync="errorDialogVisible" width="720px">
      <el-table :data="importErrors" size="small" border max-height="420">
        <el-table-column prop="rowNumber" label="行号" width="80" />
        <el-table-column prop="pointName" label="点位名称" min-width="240" show-overflow-tooltip />
        <el-table-column prop="ioaHex" label="信息体地址" width="120" />
        <el-table-column prop="message" label="原因" min-width="180" show-overflow-tooltip />
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import './electric.scss'
import {
  electricCurrent,
  electricUsageList,
  electricSummary,
  electricPointImportExcel
} from './electric-api'

export default {
  name: 'power-room-electric',
  data() {
    const end = new Date()
    const start = new Date(end.getTime() - 2 * 60 * 1000)
    return {
      query: {
        roomCode: 'NORTH_POWER_ROOM',
        cabinetCode: '',
        usageType: '',
        intervalMinutes: 2
      },
      timeRange: [this.formatDate(start), this.formatDate(end)],
      importForm: {
        channelCode: 'NORTH_POWER_104',
        defaultScaleFactor: 0.001,
        dryRun: true
      },
      currentRows: [],
      usageRows: [],
      summaryRows: [],
      currentLoading: false,
      usageLoading: false,
      summaryLoading: false,
      importResult: null,
      errorDialogVisible: false
    }
  },
  computed: {
    totalUsage() {
      return this.sumUsage(this.summaryRows)
    },
    productionUsage() {
      return this.sumUsage(this.summaryRows.filter(item => item.usageType === 'PRODUCTION'))
    },
    livingUsage() {
      return this.sumUsage(this.summaryRows.filter(item => item.usageType === 'LIVING'))
    },
    importErrors() {
      return (this.importResult && this.importResult.errors) || []
    }
  },
  mounted() {
    this.loadAll()
  },
  methods: {
    loadAll() {
      this.refreshCurrent()
      this.loadUsage()
      this.loadSummary()
    },
    refreshCurrent() {
      this.currentLoading = true
      electricCurrent({
        roomCode: this.query.roomCode,
        cabinetCode: this.query.cabinetCode,
        usageType: this.query.usageType,
        onlyEnabled: true
      }).then((res) => {
        this.currentRows = res.data.data || []
      }).finally(() => {
        this.currentLoading = false
      })
    },
    loadUsage() {
      this.usageLoading = true
      electricUsageList(this.buildRangeQuery()).then((res) => {
        this.usageRows = res.data.data || []
      }).finally(() => {
        this.usageLoading = false
      })
    },
    loadSummary() {
      this.summaryLoading = true
      electricSummary(Object.assign(this.buildRangeQuery(), { groupBy: 'USAGE_TYPE' })).then((res) => {
        this.summaryRows = res.data.data || []
      }).finally(() => {
        this.summaryLoading = false
      })
    },
    buildRangeQuery() {
      const range = this.timeRange || []
      const cabinetCodes = this.query.cabinetCode ? [this.query.cabinetCode.trim()] : []
      return {
        roomCode: this.query.roomCode,
        cabinetCodes,
        usageType: this.query.usageType,
        startTime: range[0],
        endTime: range[1],
        intervalMinutes: this.query.intervalMinutes,
        usageMode: 'EP_PLUS',
        includePartial: true
      }
    },
    handleImport(option) {
      if (!this.query.roomCode) {
        this.$message.warning('请先填写配电室编码')
        return
      }
      if (!this.importForm.channelCode) {
        this.$message.warning('请先填写104通道编码')
        return
      }
      const formData = new FormData()
      formData.append('roomCode', this.query.roomCode)
      formData.append('channelCode', this.importForm.channelCode)
      formData.append('defaultScaleFactor', this.importForm.defaultScaleFactor)
      formData.append('dryRun', this.importForm.dryRun)
      formData.append('file', option.file)
      electricPointImportExcel(formData).then((res) => {
        this.importResult = res.data.data || {}
        const errorCount = (this.importResult.errors || []).length
        if (errorCount > 0) {
          this.errorDialogVisible = true
          this.$message.warning(`导入完成，有 ${errorCount} 条错误`)
        } else {
          this.$message.success(this.importForm.dryRun ? '试导入通过' : '导入完成')
        }
        if (!this.importForm.dryRun) {
          this.loadAll()
        }
      })
    },
    sumUsage(rows) {
      return rows.reduce((sum, item) => sum + Number(item.usageKwh || 0), 0)
    },
    formatNumber(value) {
      const number = Number(value || 0)
      return number.toFixed(3)
    },
    formatDate(date) {
      const pad = value => String(value).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    }
  }
}
</script>
